import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import {
  DataPagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Text,
} from "@musa.tech/design-system";
import { ApiErrorDisplay } from "@/components/shared/ApiErrorDisplay";
import { ColetaCalendarCard } from "./ColetaCalendarCard";
import { DIA_PAGE_SIZE, useDiaColetas } from "@/hooks/useProximasColetas";
import { parseCollectDate } from "@/utils/coletasCalendar";
import type { FilterState } from "@/hooks/useFilters";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface DiaColetasModalProps {
  /** Dia (YYYY-MM-DD) a listar. null = fechado. */
  day: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterState;
  /** Abre o detalhe de uma coleta específica. */
  onSelectCollect: (collect: CollectForTable) => void;
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** Modal com TODAS as coletas de um dia (paginado, mesmos filtros). */
export const DiaColetasModal = ({
  day,
  open,
  onOpenChange,
  filters,
  onSelectCollect,
}: DiaColetasModalProps) => {
  const [page, setPage] = useState(1);

  // Reseta a paginação ao trocar de dia.
  useEffect(() => {
    setPage(1);
  }, [day]);

  const { collects, pagination, isLoading, error, refetch } = useDiaColetas({
    day,
    filters,
    page,
  });

  const parsed = day ? parseCollectDate(day) : null;
  const total = pagination?.count ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-neutral-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-neutral-700" aria-hidden />
            <Text variant="heading" size="large" className="text-neutral-900">
              {parsed
                ? capitalize(format(parsed, "EEEE, dd 'de' MMMM", { locale: ptBR }))
                : "Coletas do dia"}
            </Text>
          </DialogTitle>
          {!isLoading && total > 0 && (
            <Text variant="body" size="small" className="text-neutral-500">
              {total} {total === 1 ? "coleta" : "coletas"}
            </Text>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error ? (
            <ApiErrorDisplay error={error} onRetry={() => refetch()} context="coletas" />
          ) : isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : collects.length === 0 ? (
            <div className="rounded-lg border border-border bg-white p-8 text-center">
              <Text variant="body" className="text-neutral-500">
                Nenhuma coleta neste dia.
              </Text>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="divide-y divide-border">
                {collects.map((collect) => (
                  <ColetaCalendarCard
                    key={collect.collect_id}
                    collect={collect}
                    layout="row"
                    onSelect={onSelectCollect}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {!isLoading && !error && total > DIA_PAGE_SIZE && (
          <div className="border-t border-neutral-200 px-6 py-3">
            <DataPagination
              page={page}
              pageSize={DIA_PAGE_SIZE}
              total={total}
              onChange={setPage}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
